import type { Location } from '#/graphql/types';
import { gql } from '@apollo/client';
import { getLocation, getLocationList, getUser } from '#/graphql/queries';
import { inUseStatuses } from './constants';
import FieldDefs from '#/types/FieldDefs';
import { getBaseColumns } from '#/common/utils/baseColumns';
import { levels } from '../components/Location.Mappings';

const fields = {
  ...getBaseColumns(),
  id: {
    dataField: 'id',
    label: 'ID',
    editorType: 'TextBox',
    editorOptions: { readOnly: true },
    isRequired: true,
  },
  name: {
    dataField: 'name',
    label: 'Name',
    isRequired: true,
    editorType: 'TextBox',
  },
  notes: {
    dataField: 'notes',
    label: 'Notes',
    editorType: 'TextArea',
  },
  lat: {
    dataField: 'lat',
    label: 'Latitude',
    editorType: 'NumberBox',
    editorOptions: {
      placeholder: 'Enter a latitude',
      min: 0,
      max: 1000,
      step: 1,
      showSpinButtons: true,
    },
  },
  lng: {
    dataField: 'lng',
    label: 'Longitude',
    editorType: 'NumberBox',
    editorOptions: {
      placeholder: 'Enter a longitude',
      min: 0,
      max: 1000,
      step: 1,
      showSpinButtons: true,
    },
  },
  address: {
    dataField: 'address',
    label: 'Address',
    editorType: 'TextArea',
  },
  parentId: {
    dataField: 'parentId',
    label: 'Parent',
    isRequired: true,
    editorType: 'RelatedItems',
    editorOptions: {
      button: {
        tooltip: 'View location details',
        url: '/assets/locations/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      disabled: true,
      dataStoreOptions: {
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
        getItem: gql(getLocation),
        getItemList: gql(getLocationList),
      },
      dynamic: {
        watch: ['level'],
        build: ({ record }) => {
          if (!record?.level) return null;
          if (record.level === 'building') return ['level', '=', 'city'];
          if (record.level === 'city') return ['level', '=', 'state'];
          if (record.level === 'state') return ['level', '=', 'country'];
        },
      },
    },
  },
  level: {
    dataField: 'level',
    label: 'Type',
    editorType: 'Lookup',
    isRequired: true,
    editorOptions: {
      displayExpr: 'label',
      valueExpr: 'key',
      searchEnabled: false,
      dataSource: Object.values(levels),
    },
  },
  inUseStatus: {
    dataField: 'inUseStatus',
    label: 'In-use Status',
    editorType: 'Lookup',
    editorOptions: {
      displayExpr: 'label',
      valueExpr: 'key',
      searchEnabled: false,
      dataSource: Object.values(inUseStatuses),
    },
  },
  thirdPartyId: {
    dataField: 'thirdPartyId',
    label: 'Third Party ID',
    editorType: 'TextBox',
  },
  source: {
    dataField: 'source',
    label: 'Third Party Source',
    editorType: 'TextBox',
  },
} satisfies FieldDefs<Location>;

export default fields;
